/* eslint-disable no-nested-ternary */
/* eslint-disable prefer-const */
import { dehydrate, QueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from 'framer-motion';
import {
	SvgCalendar,
	SvgFilters,
	SvgLogo,
	SvgMapMarker,
	SvgPin,
	SvgSelectArrow,
	SvgUsers,
	SvgWallet
} from 'litto-lib/assets';
import {
	BtnRoundness,
	BtnSizes,
	BtnVariants,
	Button,
	CustomDayPickerRangeController,
	IPopperButtonRef,
	Modal,
	Pagination,
	PopperButton
} from 'litto-lib/components/common';
import {
	centsToPrice,
	checkoutDateFormat,
	formatGuests,
	formatMoreFilters,
	priceParser,
	priceToCents
} from 'litto-lib/helpers';
import {
	queryTypes,
	useHasMounted,
	useMediaQuery,
	useQueryStates,
	useScrollLock
} from 'litto-lib/hooks';
import {
	capitalize,
	getNonZeroQueries,
	getQueryString,
	isEmpty,
	isServerReq,
	objValuesToString
} from 'litto-lib/utils';
import moment from 'moment';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import useEvent from 'analytics/hooks/useEvent';
import { AccommodationCard } from 'components/accommodation';
const AccommodationsMap = dynamic(
	() => import('components/accommodation/AccommodationsMap/AccommodationsMap'),
	{ ssr: false, loading: () => <div /> }
);
import {
	GuestForm,
	IGuestFormData,
	IMoreFiltersFormData,
	IPriceRangeFormData,
	ITypeFormData,
	LocationAutoCompleteSelect,
	LocationSlider,
	ModalMoreFilters,
	PriceRangeForm,
	SkeletonCard,
	TipsSlider,
	TypeForm
} from 'components/shared';
import { BasicLayout } from 'Layout';
import {
	AccommodationJsonLd,
	BreadCrumbJsonLd,
	getHostname,
	NextSeo
} from 'seo';
import {
	mapAccommodationsToGAItems,
	mapAccommodationToGAItem
} from 'seo/utils/gtm/ga4';
import {
	getAccommodations,
	IGetAccommodationsQueryParams,
	IGetPriceRangeQueryParams,
	useGetAccommodationsQuery,
	useGetAllAccommodationAttributesQuery,
	useGetPriceRangeQuery
} from 'services/shop/accommodation';
import { IAccommodationRead } from 'services/shop/accommodation/interfaces';
import { ILocation, ILocationRead } from 'services/shop/interfaces';
import {
	getLocations,
	locationKeys,
	useGetLocationsQuery
} from 'services/shop/location';
import { useGetTaxonQuery } from 'services/shop/taxon';
import { useGetPromoBannerData } from 'services/wordpress/data';

interface IChannelAccommodationsProps {
	query: any;
	city: ILocation; // Don't name this locations because of global location object
}

const MIN_PRICE_RANGE_VALUE = 5;
const MAX_PRICE_RANGE_VALUE = 1000;

const ChannelAccommodations: React.FC<IChannelAccommodationsProps> = ({
	query,
	city
}) => {
	const router = useRouter();
	const isMobile = useMediaQuery('(max-width: 820px)');
	const hasMounted = useHasMounted();
	const isTablet = useMediaQuery('(max-width: 1023px)');
	const { event } = useEvent();

	const enableMapAnimation = React.useRef(false);
	const filterHeaderRef = React.useRef<HTMLDivElement>(null);

	const [hoveredAccommodation, setHoveredAccommodation] = React.useState('');

	const forceMapFitBounds = React.useRef(false); // force map to adjust boundaries when channel or page is changed

	const reverseForceMapFitBounds = () => {
		forceMapFitBounds.current = false; // function to reverse flag from inside of the map component
	};

	const getChannelDisplayName = () => {
		return `${query.channel
			.split('-')
			.reverse()
			.map((w: string) => capitalize(w))
			.join(' ')}`;
	};

	const [queries, updateQueries] = useQueryStates(
		{
			channel: queryTypes.string,
			page: queryTypes.integer,
			adults: queryTypes.integer,
			children: queryTypes.integer,
			infants: queryTypes.integer,
			checkin: queryTypes.string,
			checkout: queryTypes.string,
			priceFrom: queryTypes.integer,
			priceTo: queryTypes.integer,
			'numberOfBeds[gte]': queryTypes.integer,
			'numberOfBaths[gte]': queryTypes.integer,
			'numberOfBedrooms[gte]': queryTypes.integer,
			amenities: queryTypes.string,
			type: queryTypes.string,
			geolocation: queryTypes.string,
			zoom: queryTypes.integer
		},
		{ history: 'push' }
	);

	let {
		channel,
		page,
		adults,
		children,
		infants,
		checkin,
		checkout,
		priceFrom,
		priceTo,
		zoom,
		type,
		...otherQueries
	} = queries;

	adults = adults || 0;
	children = children || 0;
	infants = infants || 0;
	const {
		'numberOfBeds[gte]': numOfBeds,
		'numberOfBaths[gte]': numOfBaths,
		'numberOfBedrooms[gte]': numOfBedrooms,
		amenities
	} = otherQueries;

	const { data: accommodations, isLoading } = useGetAccommodationsQuery({
		params: getNonZeroQueries<IGetAccommodationsQueryParams>({
			page: page || query.page || 1,
			itemsPerPage: 12,
			location: channel || (query.channel as string),
			tripDates:
				checkin && checkout
					? `${checkin}-${checkout}`
					: query.checkin && query.checkout
					? `${query.checkin}-${query.checkout}`
					: null,
			'numberOfGuests[gte]':
				adults + children
					? String(adults + children)
					: query.adults + query.children
					? String(query.adults + query.children)
					: null,
			'numberOfInfants[gte]': infants
				? String(infants)
				: query.infants
				? String(query.infants)
				: null,
			priceFrom: priceFrom
				? String(priceToCents(priceFrom))
				: query.priceFrom
				? String(priceToCents(query.priceFrom))
				: 0,
			priceTo: priceTo
				? String(priceToCents(priceTo))
				: query.priceTo
				? String(priceToCents(query.priceTo))
				: 0,
			type: type || query.type,
			...objValuesToString(otherQueries)
		})
	});

	const {
		data: accommodationAttributes,
		refetch: getAllAccommodationAttributes
	} = useGetAllAccommodationAttributesQuery();

	const { data: accommodationTypes, refetch: getAllAccommodationTypes } =
		useGetTaxonQuery('accommodation');

	const { data: promoBanner } = useGetPromoBannerData();

	const { data: cities } = useGetLocationsQuery();
	const selectedCity = cities?.['hydra:member']?.find(
		(city: ILocationRead) => city.code === query.channel
	);
	const otherCities = useMemo(() => {
		if (cities && !isEmpty(cities['hydra:member'])) {
			return cities['hydra:member']
				?.filter(city => city.code !== query.channel)
				?.slice(0, 4);
		}
	}, [cities]);

	const guestsFormRef = React.useRef(null);
	const onGuestsSubmit = (guestsData: IGuestFormData) => {
		const { adults, children, infants } = guestsData;
		updateQueries({
			...queries,
			page: 1,
			adults: adults || null,
			children: children || null,
			infants: infants || null
		});
	};

	const clearGuestsForm = () => {
		(guestsFormRef?.current as any)?.setValues({
			adults: 1,
			children: 0,
			infants: 0
		});
	};

	const typesFormRef = React.useRef(null);
	const onTypesSubmit = (typesData: ITypeFormData) => {
		const { types } = typesData;
		updateQueries({
			...queries,
			page: 1,
			type: types || null
		});
	};
	const getTypesText = React.useMemo(() => {
		if (type) {
			return `${capitalize(type?.split(',')?.[0]?.replace(/-|_/g, ' '))} ${
				type?.split(',')?.length > 1
					? '+' + String(type?.split(',')?.length - 1)
					: ''
			}`;
		} else {
			return 'Type of place';
		}
	}, [type]);

	const clearTypesForm = () => {
		(typesFormRef?.current as any)?.setValues({
			types: ''
		});
	};

	const locationRef: IPopperButtonRef = React.useRef(null);
	const priceFormRef = React.useRef(null);
	const pricePopperRef = React.useRef<any>(null);
	const addDatesRef = React.useRef(null);

	const { data: priceRange } = useGetPriceRangeQuery({
		params: getNonZeroQueries<IGetPriceRangeQueryParams>({
			page: page || query.page || 1,
			itemsPerPage: 12,
			location: channel || (query.channel as string),
			tripDates:
				checkin && checkout
					? `${checkin}-${checkout}`
					: query.checkin && query.checkout
					? `${query.checkin}-${query.checkout}`
					: null,
			'numberOfGuests[gte]':
				adults + children
					? String(adults + children)
					: query.adults + query.children
					? String(query.adults + query.children)
					: null,
			'numberOfInfants[gte]': infants
				? String(infants)
				: query.infants
				? String(query.infants)
				: null,
			priceFrom: priceFrom
				? String(priceToCents(priceFrom))
				: query.priceFrom
				? String(priceToCents(query.priceFrom))
				: 0,
			priceTo: priceTo
				? String(priceToCents(priceTo))
				: query.priceTo
				? String(priceToCents(query.priceTo))
				: 0,
			type: type || query.type,
			...objValuesToString(otherQueries)
		})
	});

	const onPriceSubmit = (guestsData: IPriceRangeFormData) => {
		const { range } = guestsData;
		const { min: priceFrom, max: priceTo } = range;
		updateQueries({
			...queries,
			page: 1,
			priceFrom:
				priceFrom && priceFrom !== MIN_PRICE_RANGE_VALUE ? priceFrom : null,
			priceTo: priceTo && priceTo !== MAX_PRICE_RANGE_VALUE ? priceTo : null
		});
	};

	const clearPriceForm = () => {
		(priceFormRef?.current as any)?.setFieldValue('range', {
			min: 0,
			max: 1000
		});
	};

	const getPriceRangeText = React.useCallback(() => {
		const defaultText = 'Price range';
		if (!priceFrom || !priceTo) {
			return defaultText;
		}
		if (
			priceFrom === MIN_PRICE_RANGE_VALUE &&
			priceTo === MAX_PRICE_RANGE_VALUE
		) {
			return defaultText;
		}
		if (
			(!priceFrom && !priceTo) ||
			(priceFrom === MIN_PRICE_RANGE_VALUE && priceTo === MAX_PRICE_RANGE_VALUE)
		) {
			return defaultText;
		}

		if (priceFrom && priceFrom > MIN_PRICE_RANGE_VALUE && !priceTo) {
			return `${priceFrom} € +`;
		}

		if (priceTo && priceTo < MAX_PRICE_RANGE_VALUE && !priceFrom) {
			return `Up to ${priceTo} €`;
		}

		return `${priceParser(priceFrom * 100)} - ${priceParser(
			(priceTo || MAX_PRICE_RANGE_VALUE) * 100
		)}`;
	}, [priceFrom, priceTo, MIN_PRICE_RANGE_VALUE, MAX_PRICE_RANGE_VALUE]);

	const onMoreFiltersSubmit = (moreFiltersData: IMoreFiltersFormData) => {
		const { numberOfBeds, numberOfBaths, numberOfBedrooms, amenities } =
			moreFiltersData;
		updateQueries({
			...queries,
			page: 1,
			'numberOfBeds[gte]': numberOfBeds || null,
			'numberOfBaths[gte]': numberOfBaths || null,
			'numberOfBedrooms[gte]': numberOfBedrooms || null,
			amenities: amenities || null
		});
	};

	const goToCityPage = (loc: ILocationRead) => {
		forceMapFitBounds.current = true;
		router.push(`/${loc.code}/accommodations`);
	};

	const [locationModalOpened, setLocationModalOpened] = React.useState(false);
	const [filtersModalOpen, setFiltersModalOpen] = React.useState(false);
	const [guestsModal, setGuestModalIsOpen] = React.useState(false);
	const [typesModal, setTypesModalIsOpen] = React.useState(false);
	const [priceRangeModal, setPriceRangeModal] = React.useState(false);

	// Date picker
	const calendarRef = React.useRef<any>(null);

	const onDateSubmit = (startDate: moment.Moment, endDate: moment.Moment) => {
		updateQueries({
			...queries,
			page:1,
			checkin: startDate ? startDate?.format(checkoutDateFormat) : null,
			checkout: endDate ? endDate?.format(checkoutDateFormat) : null,
			priceFrom: !startDate && !endDate ? null : priceFrom,
			priceTo: !startDate && !endDate ? null : priceTo
		});
	};

	const clearDates = () => {
		calendarRef?.current?.clearDates();
	};

	const applyDates = () => {
		calendarRef?.current?.submitDates(onDatePickerClose);
		calendarRef?.current?.closeCalendar();
	};

	const [searchTerm, setSearchTerm] = React.useState('');

	const onDatePickerClose = (final: {
		startDate: moment.Moment;
		endDate: moment.Moment;
	}) => {
		const { startDate, endDate } = final;
		if (startDate && !endDate) {
			onDateSubmit(startDate, moment(startDate).add(1, 'days'));
		} else {
			onDateSubmit(startDate, endDate);
		}
	};

	const onPageChange = ({ selected }: { selected: number }) => {
		forceMapFitBounds.current = true;
		updateQueries({
			...queries,
			page: selected + 1
		});
	};
	const selectedLocation = cities?.['hydra:member'].find(city =>
		[query.channel, channel].includes(city.code)
	);

	const handleMapDragAndZoom = (geoLocationData: string, zoom: number) => {
		updateQueries({
			...queries,
			geolocation: geoLocationData,
			zoom: zoom,
			page: null
		});
	};

	const [mapShown, setMapShown] = React.useState<boolean>(false);

	const [ref, inView] = useInView({
		triggerOnce: false,
		threshold: 0
	});

	const testRef = React.useRef<HTMLDivElement>(null);
	useScrollLock<HTMLDivElement | unknown>(mapShown, testRef);

	React.useEffect(() => {
		if (router.prefetch) {
			router.prefetch(router.pathname, router.asPath, { locale: 'en' });
		}
		getAllAccommodationAttributes();
		getAllAccommodationTypes();
	}, []);

	React.useEffect(() => {
		if (typeof window !== 'undefined') {
			window.addEventListener('resize', () => setMapShown(false));
		}
	}, []);

	const filterBtnProps: {
		variant: BtnVariants;
		size: BtnSizes;
		rounded: BtnRoundness;
	} = {
		variant: 'outline',
		size: 'sm',
		rounded: 'full'
	};

	React.useEffect(() => {
		if (!isEmpty(accommodations?.['hydra:member'])) {
			const items = mapAccommodationsToGAItems(
				accommodations?.['hydra:member'] || [],
				page ? page * 12 - 12 : 0
			); // Change 12 if itemsPerPage Changes );
			event('view_item_list', {
				item_list_id: `accommodation_${getChannelDisplayName()}`,
				item_list_name: `Accommodations - ${getChannelDisplayName()}`,
				items
			});
		}
	}, [accommodations?.['hydra:member']]);

	const onAccommodationClick = (item: IAccommodationRead, position: number) => {
		const accommodationData = mapAccommodationToGAItem(item, position);
		event('select_item', {
			item_list_id: `accommodation_${getChannelDisplayName()}`,
			item_list_name: `Accommodations - ${getChannelDisplayName()}`,
			items: [
				{
					...accommodationData,
					item_list_id: `accommodation_${getChannelDisplayName()}`,
					item_list_name: `Accommodations - ${getChannelDisplayName()}`,
					index: page ? page * 12 - 12 + position : position
				}
			]
		});
	};

	return (
		<BasicLayout noHeader={true}>
			<NextSeo
				// title={`${getChannelDisplayName()} - Stays - Litto`}
				openGraph={{
					type: 'website',
					url: `${getHostname()}/accommodations`
				}}
				title={`${selectedCity?.translation.name}, ${selectedCity?.county},  Croatia | Book apartments, luxury villas and vacation rentals - LITTO`}
				description={`Best price guaranteed! Schedule your ${selectedCity?.translation.name}, Croatia holiday with Litto and discover unforgettable holiday experiences on the Adriatic coast.`}
			/>
			<BreadCrumbJsonLd
				itemListElements={[
					{
						item: `${getHostname()}/${query.channel}/accommodations`,
						name: 'Litto.co',
						position: 1
					},
					{
						item: `${getHostname()}/${query.channel}/accommodations`,
						name: `${capitalize(query.channel.split('-')[0])}`,
						position: 2
					},
					{
						item: `${getHostname()}/${query.channel}/accommodations`,
						name: `Accommodations in ${city?.county}`,
						position: 3
					},

					{
						item: `${getHostname()}/${query.channel}/accommodations`,
						name: `Accommodations in ${capitalize(
							query.channel.split('-')[1]
						)}`,
						position: 4
					}
				]}
			/>

			{!isEmpty(accommodations?.['hydra:member']) && (
				<>
					{accommodations?.['hydra:member']?.map(accommodation => {
						return (
							<AccommodationJsonLd
								key={accommodation.code}
								code={accommodation.code}
								name={accommodation?.translation?.name}
								image={accommodation?.mainImage?.imageUrl}
							/>
						);
					})}
				</>
			)}

			<div ref={ref}>
				<AnimatePresence>
					{isMobile && inView && (
						<motion.button
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.3 }}
							onClick={() => {
								if (!enableMapAnimation.current) {
									enableMapAnimation.current = true;
								}
								setMapShown(!mapShown);
							}}
							className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center justify-center rounded-lg bg-white px-5 py-2 text-sm font-bold text-white shadow-lg"
						>
							<SvgMapMarker className="text-primary-700" />
							<p className="ml-3 text-primary-700">
								Switch to {mapShown ? 'list' : 'map'}
							</p>
						</motion.button>
					)}
				</AnimatePresence>
				<div
					ref={filterHeaderRef}
					className={`sticky top-0 ${
						mapShown && isMobile ? '-mt-20' : 'mt-0'
					} z-40 h-20 w-full border-b border-gray-100 bg-white transition-all duration-500`}
				>
					<div
						className={` flex items-center overflow-y-visible overflow-x-scroll lg:overflow-x-auto ${
							isMobile ? 'container' : 'px-4 md:px-10'
						} py-4`}
					>
						<Link href="/">
							<a className="mr-5" aria-label="Litto">
								<SvgLogo className="text-primary-500" />
							</a>
						</Link>
						{!isMobile ? (
							<PopperButton
								innerRef={locationRef}
								text={selectedLocation?.translation?.name || 'Select location'}
								noPadding
								customToggle={isActive => (
									<Button
										className="mr-2"
										color={isActive ? 'primary' : 'dark'}
										{...filterBtnProps}
										onClick={() => setLocationModalOpened(!locationModalOpened)}
										iconLeft={<SvgPin />}
										iconRight={
											<SvgSelectArrow
												className={`scale-65 transform ${
													isActive ? 'text-primary-500' : 'opacity-50'
												}`}
											/>
										}
									/>
								)}
							>
								<LocationAutoCompleteSelect
									className="mt-6"
									isOpen={locationModalOpened}
									onSelectOption={loc => {
										goToCityPage(loc);
										locationRef?.current?.closePopper();
									}}
									setSearchTerm={setSearchTerm}
									searchElementProps={{
										id: 'location',
										name: 'location',
										value: searchTerm,
										placeholder: 'Search for a location'
									}}
								/>
							</PopperButton>
						) : (
							<>
								<Button
									text={
										selectedLocation?.translation?.name || 'Select location'
									}
									className="mr-2"
									color="primary"
									{...filterBtnProps}
									onClick={() => setLocationModalOpened(true)}
									iconLeft={<SvgPin />}
									iconRight={
										<SvgSelectArrow
											className={`scale-65 transform ${
												locationModalOpened ? 'text-primary-500' : 'opacity-50'
											}`}
										/>
									}
								/>
								<Modal
									noPadding
									headerText="Select Location"
									fullHeight
									innerScrollSelector=".overflow-y-auto"
									isOpen={locationModalOpened}
									onRequestClose={() => setLocationModalOpened(false)}
								>
									<LocationAutoCompleteSelect
										className="mt-6 md:mt-0 lg:mt-6"
										searchElementProps={{
											placeholder: 'Search for a location',
											id: 'city',
											name: 'city',
											value: searchTerm
										}}
										setSearchTerm={setSearchTerm}
										onSelectOption={loc => {
											goToCityPage(loc);
											setLocationModalOpened(false);
											setMapShown(false);
										}}
										isOpen={locationModalOpened}
										setIsOpen={setLocationModalOpened}
									/>
								</Modal>
							</>
						)}
						<CustomDayPickerRangeController
							innerRef={calendarRef}
							initialStartDate={
								checkin
									? moment(checkin || query.checkin, checkoutDateFormat)
									: null
							}
							initialEndDate={
								checkout
									? moment(checkout || query.checkout, checkoutDateFormat)
									: null
							}
							offset={[-10, 10]}
							placement="bottom-start"
							initialVisibleMonth={() => moment()}
							minDate={moment().add(-1, 'days')}
							isDayBlocked={day => day.isBefore(moment().add(-1, 'days'))}
							showInputs
							closeOnOutsideClick={!isMobile}
							slotBottom={
								<div className="flex justify-end">
									<Button
										text="Clear"
										onClick={clearDates}
										color="transparent"
									/>
									<Button
										onClick={applyDates}
										text="Apply"
										color="primary"
										data-popper-close
									/>
								</div>
							}
							customStartDateInput={({ value, isActive, onClick }) => {
								return (
									<Button
										text={value ? value : 'Check in'}
										iconLeft={<SvgCalendar />}
										color={isActive ? 'primary' : 'dark'}
										{...filterBtnProps}
										onClick={onClick}
									/>
								);
							}}
							customEndDateInput={({ value, isActive, onClick }) => {
								return (
									<Button
										text={value ? value : 'Check Out'}
										color={isActive ? 'primary' : 'dark'}
										{...filterBtnProps}
										onClick={onClick}
									/>
								);
							}}
						/>
						{/* GUESTS SELECT */}
						{!isMobile && (
							<PopperButton
								text={
									(adults && `${formatGuests(adults, children, infants)}`) ||
									'Guests'
								}
								size="lg"
								customToggle={isActive => (
									<Button
										iconLeft={<SvgUsers />}
										color={isActive ? 'primary' : 'dark'}
										{...filterBtnProps}
										className="ml-2"
									/>
								)}
							>
								<GuestForm
									formRef={guestsFormRef}
									initialValues={{
										adults: adults || 1,
										children: children || 0,
										infants: infants || 0
									}}
									enableReinitialize
									onSubmit={onGuestsSubmit}
								/>
								<div className="-mx-10 -mb-7 flex justify-between border-t px-10 pt-3">
									<Button
										className="-ml-7"
										text="Clear"
										color="transparent"
										type="button"
										onClick={clearGuestsForm}
									/>
									<Button
										text="Apply"
										color="primary"
										type="submit"
										onClick={() => {
											(guestsFormRef?.current as any)?.submitForm();
										}}
										data-popper-close
									/>
								</div>
							</PopperButton>
						)}
						{isMobile && (
							<React.Fragment>
								<Button
									text={
										(adults && `${formatGuests(adults, children, infants)}`) ||
										'Guests'
									}
									onClick={() => setGuestModalIsOpen(true)}
									color="dark"
									{...filterBtnProps}
									iconLeft={<SvgUsers />}
									size="sm"
									className="ml-2 md:hidden"
								/>
								<Modal
									headerText={'Guests'}
									isOpen={guestsModal}
									onRequestClose={() => setGuestModalIsOpen(false)}
								>
									<GuestForm
										formRef={guestsFormRef}
										initialValues={{
											adults: adults || 1,
											children: children || 0,
											infants: infants || 0
										}}
										enableReinitialize
										onSubmit={onGuestsSubmit}
									/>
									<Modal.Footer key="ModalFooter">
										<div className="flex justify-between">
											<Button
												text="Clear"
												color="transparent"
												className="-ml-7"
												type="button"
												onClick={clearGuestsForm}
											/>
											<Button
												text="Apply"
												color="primary"
												type="submit"
												onClick={() => {
													setGuestModalIsOpen(false);
													(guestsFormRef?.current as any)?.submitForm();
												}}
											/>
										</div>
									</Modal.Footer>
								</Modal>
							</React.Fragment>
						)}
						{/* TYPE SELECT */}
						{!isMobile && (
							<PopperButton
								text={getTypesText}
								size="lg"
								customToggle={isActive => (
									<Button
										iconLeft={<SvgUsers />}
										color={isActive ? 'primary' : 'dark'}
										{...filterBtnProps}
										className="ml-2"
									/>
								)}
							>
								<TypeForm
									initialValues={{
										types: type || ''
									}}
									onSubmit={onTypesSubmit}
									formRef={typesFormRef}
									accommodationTypes={accommodationTypes?.children || []}
								/>
								<div className="-mx-10 -mb-7 flex justify-between border-t px-10 pt-3">
									<Button
										className="-ml-7"
										text="Clear"
										color="transparent"
										type="button"
										onClick={clearTypesForm}
									/>
									<Button
										text="Apply"
										color="primary"
										type="submit"
										onClick={() => {
											(typesFormRef?.current as any)?.submitForm();
										}}
										data-popper-close
									/>
								</div>
							</PopperButton>
						)}
						{isMobile && (
							<React.Fragment>
								<Button
									text="Type of place"
									onClick={() => setTypesModalIsOpen(true)}
									color="dark"
									{...filterBtnProps}
									iconLeft={<SvgUsers />}
									size="sm"
									className="ml-2 md:hidden"
								/>
								<Modal
									headerText="Type of place"
									isOpen={typesModal}
									onRequestClose={() => setTypesModalIsOpen(false)}
								>
									<TypeForm
										initialValues={{
											types: type || ''
										}}
										onSubmit={onTypesSubmit}
										formRef={typesFormRef}
										accommodationTypes={accommodationTypes?.children || []}
									/>
									<Modal.Footer key="ModalFooter">
										<div className="flex justify-between">
											<Button
												text="Clear"
												color="transparent"
												className="-ml-7"
												type="button"
												onClick={clearTypesForm}
											/>
											<Button
												text="Apply"
												color="primary"
												type="submit"
												onClick={() => {
													setTypesModalIsOpen(false);
													(typesFormRef?.current as any)?.submitForm();
												}}
											/>
										</div>
									</Modal.Footer>
								</Modal>
							</React.Fragment>
						)}
						{/* PRICE SELECT */}
						{!isMobile && (
							<PopperButton
								innerRef={pricePopperRef}
								text={getPriceRangeText()}
								size="lg"
								customToggle={isActive => {
									return (
										<Button
											iconLeft={<SvgWallet />}
											color={isActive ? 'primary' : 'dark'}
											{...filterBtnProps}
											className="ml-2 "
										/>
									);
								}}
							>
								{(hasMounted && checkin && checkout && (
									<>
										<PriceRangeForm
											formRef={priceFormRef}
											minValue={MIN_PRICE_RANGE_VALUE}
											maxValue={MAX_PRICE_RANGE_VALUE}
											averagePrice={centsToPrice(priceRange?.averagePrice || 0)}
											initialValues={{
												range: {
													min: priceFrom || MIN_PRICE_RANGE_VALUE,
													max: priceTo || MAX_PRICE_RANGE_VALUE
												}
											}}
											enableReinitialize
											onSubmit={onPriceSubmit}
										/>
										<div className="-mx-10 -mb-7 flex justify-between border-t px-10 pt-3">
											<Button
												text="Clear"
												className="-ml-7"
												color="transparent"
												type="button"
												onClick={clearPriceForm}
											/>
											<Button
												text="Apply"
												onClick={() => {
													(priceFormRef?.current as any)?.submitForm();
												}}
												color="primary"
												data-popper-close
											/>
										</div>
									</>
								)) || (
									<div className="flex flex-col items-center justify-center">
										<span className="mb-5">
											Add your trip dates to get prices
										</span>
										<Button
											ref={addDatesRef}
											text="Add dates"
											size="sm"
											onClick={() => {
												pricePopperRef?.current?.closePopper();
												calendarRef?.current?.openCalendar();
											}}
											color="primary"
											data-calendar-skip-close
											// data-popper-close
										/>
									</div>
								)}
							</PopperButton>
						)}
						{isMobile && (
							<React.Fragment>
								<Button
									onClick={() => setPriceRangeModal(true)}
									text={getPriceRangeText()}
									iconLeft={<SvgWallet />}
									color={priceRangeModal ? 'primary' : 'dark'}
									{...filterBtnProps}
									className="ml-2"
								/>
								<Modal
									headerText="Price range"
									isOpen={priceRangeModal}
									onRequestClose={() => setPriceRangeModal(false)}
								>
									{(checkin && checkout && (
										<>
											<PriceRangeForm
												formRef={priceFormRef}
												minValue={MIN_PRICE_RANGE_VALUE}
												maxValue={MAX_PRICE_RANGE_VALUE}
												averagePrice={centsToPrice(
													priceRange?.averagePrice || 0
												)}
												initialValues={{
													range: {
														min: priceFrom || MIN_PRICE_RANGE_VALUE,
														max: priceTo || MAX_PRICE_RANGE_VALUE
													}
												}}
												enableReinitialize
												onSubmit={onPriceSubmit}
											/>
											<Modal.Footer key="ModalFooter">
												<div className="-mb-7 flex justify-between border-t pt-3">
													<Button
														data-popper-close
														className="-ml-7"
														text="Clear"
														color="transparent"
														onClick={clearPriceForm}
													/>
													<Button
														text="Apply"
														onClick={() => {
															(priceFormRef?.current as any)?.submitForm();
															setPriceRangeModal(false);
														}}
														color="primary"
														data-popper-close
													/>
												</div>
											</Modal.Footer>
										</>
									)) || (
										<div className="flex flex-col items-center justify-center">
											<span className="mb-5">
												Add your trip dates to get prices
											</span>
											<Button
												// ref={addDatesRef}
												text="Add dates"
												size="sm"
												onClick={() => {
													(calendarRef?.current as any)?.openCalendar();
													setPriceRangeModal(false);
												}}
												color="primary"
												data-popper-close
											/>
										</div>
									)}
								</Modal>
							</React.Fragment>
						)}

						<Button
							onClick={() => setFiltersModalOpen(true)}
							text={formatMoreFilters(otherQueries)}
							iconLeft={<SvgFilters />}
							color={filtersModalOpen ? 'primary' : 'dark'}
							{...filterBtnProps}
							className="ml-2"
						/>

						<ModalMoreFilters
							isOpen={filtersModalOpen}
							attributes={accommodationAttributes?.['hydra:member'] || []}
							onRequestClose={() => setFiltersModalOpen(false)}
							onSubmit={onMoreFiltersSubmit}
						/>
					</div>
				</div>

				<div className={`flex justify-between ${isMobile ? '' : 'gap-10'}`}>
					<div
						className={`my-8 ${isMobile ? 'container w-full' : 'w-1/2 pl-10'}`}
					>
						{isLoading && (
							<div className={`flex flex-wrap`}>
								<div className="mb-8 h-10 w-2/5 animate-pulse bg-gray-300" />
								{Array.from(Array(12).keys()).map(v => {
									return (
										<SkeletonCard
											orientation={`${isTablet ? 'vertical' : 'horizontal'}`}
											aspectRatio="9x5"
											key={v}
											className={`mb-8 w-full last:mb-0 sm:mb-9.5 `}
										/>
									);
								})}
							</div>
						)}

						{!isLoading && !isEmpty(accommodations?.['hydra:member']) && (
							<>
								<div className="flex flex-wrap">
									<p className="mb-8 text-2xl font-bold">
										{accommodations?.['hydra:totalItems']}{' '}
										{accommodations?.['hydra:totalItems'] === 1
											? 'stay'
											: 'stays'}{' '}
										{!queries.geolocation && selectedLocation?.translation?.name
											? `in ${selectedLocation?.translation?.name}`
											: ''}
									</p>

									{accommodations?.['hydra:member'].map((product, i) => {
										return (
											<AccommodationCard
												onMouseEnter={() => {
													setHoveredAccommodation(product?.code);
												}}
												onMouseLeave={() => {
													setHoveredAccommodation('');
												}}
												orientation={`${isTablet ? 'vertical' : 'horizontal'}`}
												key={product['@id']}
												className={`mb-8 w-full sm:mb-9.5 `}
												product={product}
												withDescription
												truncate={3}
												onClick={() => {
													onAccommodationClick(product, i + 1);
												}}
											/>
										);
									})}
								</div>

								<Pagination
									className="flex w-full justify-center"
									onPageChange={onPageChange}
									disableInitialCallback
									hrefBuilder={pageIndex => {
										const queryString = getQueryString(
											getNonZeroQueries({
												page: pageIndex,
												adults: adults || query.adults || null,
												children: children || query.children || null,
												infants: infants || query.infants || null,
												checkin: checkin || query.checkin || null,
												checkout: checkout || query.checkout || null,
												priceFrom: priceFrom || query.priceFrom || null,
												priceTo: priceTo || query.priceTo || null,
												'numberOfBeds[gte]':
													numOfBeds || query['numberOfBeds[gte]'] || null,
												'numberOfBaths[gte]':
													numOfBaths || query['numberOfBaths[gte]'] || null,
												'numberOfBedrooms[gte]':
													numOfBedrooms ||
													query['numberOfBedrooms[gte]'] ||
													null,
												amenities: amenities || query.amenities || null,
												type: type || query.type || null
											})
										);
										return `/${query.channel}${
											queryString ? '?' + queryString : ''
										}`;
									}}
									pageCount={
										Math.ceil(
											Math.round(accommodations?.['hydra:totalItems'] || 0) / 12
										) || 1
									}
									pageRangeDisplayed={2}
									marginPagesDisplayed={0}
									initialPage={page ? page - 1 : 0}
								/>
							</>
						)}
						{!isLoading && isEmpty(accommodations?.['hydra:member']) && (
							<div className="py-20">
								<h3 className="mb-1 text-2xl">Nothing to see here.</h3>
								<p className="mb-3">Adjust your dates to get better results</p>
								<Button
									size="sm"
									className="self-start"
									href={`/${query.channel}/accommodations`}
								>
									Clear filters
								</Button>
							</div>
						)}
					</div>

					<div
						ref={testRef}
						className={`
							right-0 z-30 overflow-hidden ${
								enableMapAnimation.current ? 'transition-all duration-500' : ''
							}
							${
								isMobile
									? 'fixed bottom-0 h-screen  max-h-screen w-full  transform'
									: 'sticky top-0 h-screen w-1/2'
							}
							${!isMobile || mapShown ? 'translate-y-0' : 'translate-y-full'}
							`}
						style={{
							top: '80px',
							height: 'calc(100vh - ' + (isMobile ? '0' : '80') + 'px)'
						}}
					>
						<AccommodationsMap
							// key={query.channel}
							className={`h-full w-full`}
							initialPage={page || 1}
							initialZoom={zoom || undefined}
							initialGeolocationCoordinates={queries.geolocation || undefined}
							markers={accommodations?.['hydra:member']} //can be undefined beacuse the items on the map won't render if we use fallback value "accommodations?.['hydra:member'] || ["
							handleMapDragAndZoom={handleMapDragAndZoom}
							hoveredAccommodation={hoveredAccommodation}
							forceMapFitBounds={forceMapFitBounds.current}
							reverseForceMapFitBounds={reverseForceMapFitBounds}
							filterHeaderHeight={filterHeaderRef?.current?.clientHeight || 0}
						/>
					</div>
				</div>
			</div>
			<>
				{promoBanner && (
					<TipsSlider tipsArray={promoBanner?.acf?.promo_banner} />
				)}
				<h2 className="container mt-10 mb-5 text-2xl font-bold md:mt-20">
					Didn’t find anything breathtaking? Explore similar cities!{' '}
				</h2>
				<LocationSlider className="mb-20" cities={otherCities} />
			</>
		</BasicLayout>
	);
};

export const getServerSideProps: GetServerSideProps = async ({
	query,
	req
}: any) => {
	const queryClient = new QueryClient();

	if (isServerReq(req)) {
		const params = getNonZeroQueries({
			page: query.page || 1,
			itemsPerPage: 12,
			location: query.channel as string,
			tripDates:
				query.checkin && query.checkout
					? `${query.checkin}-${query.checkout}`
					: null,
			'numberOfGuests[gte]':
				query.adults + query.children
					? String(query.adults + query.children)
					: null,
			'numberOfInfants[gte]': query.infants ? String(query.infants) : null,
			priceFrom: query.priceFrom ? String(priceToCents(query.priceFrom)) : 0,
			priceTo: query.priceTo ? String(priceToCents(query.priceTo)) : 0,
			type: query.type || null,
			'numberOfBeds[gte]': query['numberOfBeds[gte]'] || null,
			'numberOfBaths[gte]': query['numberOfBaths[gte]'] || null,
			'numberOfBedrooms[gte]': query['numberOfBedrooms[gte]'] || null,
			amenities: query.amenities || null,
			geolocation: query.geolocation || null
		} as IGetAccommodationsQueryParams);

		const [accommodations, cities] = await Promise.all([
			getAccommodations({
				params
			}),
			getLocations()
		]);
		await Promise.all([
			queryClient.prefetchQuery(
				['accommodations', ...Object.entries(params || {}).sort()],
				() => {
					return accommodations;
				}
			),
			queryClient.prefetchQuery(locationKeys.list({}), () => {
				return cities;
			})
		]);
	}
	return {
		props: {
			query,
			dehydratedState: dehydrate(queryClient)
		}
	};
};

export default ChannelAccommodations;
